import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, Plus, X, ListTodo } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { getInitials } from '../../lib/people';
import { shortcutTypes, type FlagTypeDef } from '../../lib/flags';
import { FlagIcon, useFixedPosition } from './FlagIcon';
import { DismissBackdrop } from '../DismissBackdrop';

/**
 * The Flag menu (#316): the types with their Alt keys, a person list for
 * Discuss with, and (when the host passes `renderNewType`) a form for a new
 * type. Opened from the toolbar, a right-click, a margin button or Alt+4.
 */

export interface FlagMenuProps {
  x: number;
  y: number;
  types: FlagTypeDef[];
  active: { types: Set<string>; persons: Set<string> };
  initialMode?: 'kinds' | 'people' | 'new';
  title?: string;
  onPick: (type: string, personId?: string) => void;
  onClear?: () => void;
  onClose: () => void;
  /** The new-type form; the menu item is hidden without it. */
  renderNewType?: (done: () => void) => ReactNode;
  /** Named lists (#337): file this line into a list; hidden without it. */
  onAddToList?: () => void;
}

interface Person { id: string; name: string }

export function FlagMenu({ x, y, types, active, initialMode = 'kinds', title, onPick, onClear, onClose, renderNewType, onAddToList }: FlagMenuProps) {
  const [mode, setMode] = useState(initialMode);
  const [people, setPeople] = useState<Person[] | null>(null);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const pos = useFixedPosition(ref, x, y);
  const visible = types.filter((t) => !t.archived);
  const keys = new Map(shortcutTypes(types).map((t, i) => [t.id, i + 1]));

  useEffect(() => {
    if (mode !== 'people' || people) return;
    apiGet<Person[]>('/people').then(setPeople).catch(() => setPeople([]));
  }, [mode, people]);

  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>(mode === 'people' ? 'input' : '[role="menuitem"]');
    first?.focus({ preventScroll: true });
  }, [mode]);

  useEffect(() => {
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [onClose]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (people ?? []).filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [people, query]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus();
  };

  const addPerson = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      const p = await apiPost<Person>('/people', { name });
      onPick('discuss', p.id);
    } catch { /* the toast comes from the caller's next action; keep the menu open */ }
  };

  const item = 'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-left text-text-primary hover:bg-bg-hover focus:bg-bg-hover focus:outline-none';
  const label = 'px-2 pt-1.5 pb-1 text-[11px] uppercase tracking-wider text-text-muted';
  const exact = shown.some((p) => p.name.toLowerCase() === query.trim().toLowerCase());

  return createPortal(
    <>
    {/* #121: a tap outside only closes the menu - it does not also land on what is underneath. */}
    <DismissBackdrop zClass="z-[49]" onDismiss={onClose} />
    <div ref={ref} role="menu" aria-label="Flags" onKeyDown={onKey}
      className="fixed z-50 w-64 max-w-[calc(100vw-16px)] rounded-lg border border-border-hover bg-bg-surface p-1 shadow-xl"
      style={{ left: pos.left, top: pos.top }}>
      {mode === 'kinds' && (
        <>
          <div className={label}>{title ?? 'Add a flag'}</div>
          {visible.map((t) => {
            const on = active.types.has(t.id) && t.id !== 'discuss';
            return (
              <button key={t.id} type="button" role="menuitem" aria-checked={on} className={item}
                onClick={() => (t.id === 'discuss' ? setMode('people') : onPick(t.id, undefined))}>
                <FlagIcon def={t} />
                <span className="flex-1">{t.label}{t.id === 'discuss' ? '...' : ''}</span>
                {on && <Check size={14} className="text-success" aria-hidden />}
                {keys.has(t.id) && <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Alt+{keys.get(t.id)}</kbd>}
              </button>
            );
          })}
          {(renderNewType || onClear || onAddToList) && <div className="my-1 h-px bg-border" />}
          {onAddToList && (
            <button type="button" role="menuitem" className={item} onClick={onAddToList}>
              <ListTodo size={16} className="text-text-secondary" aria-hidden /><span className="flex-1">Add to list...</span>
              <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Alt+L</kbd>
            </button>
          )}
          {renderNewType && (
            <button type="button" role="menuitem" className={item} onClick={() => setMode('new')}>
              <Plus size={16} className="text-text-secondary" aria-hidden /><span className="flex-1">New type...</span>
            </button>
          )}
          {onClear && (
            <button type="button" role="menuitem" className={item} onClick={onClear}>
              <X size={16} className="text-text-secondary" aria-hidden /><span className="flex-1">Remove all from this line</span>
              <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Alt+0</kbd>
            </button>
          )}
        </>
      )}
      {mode === 'people' && (
        <>
          <button type="button" className={item} onClick={() => setMode('kinds')}>
            <ChevronLeft size={16} aria-hidden /><span>Discuss with...</span>
          </button>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person" aria-label="Find a person"
            onKeyDown={(e) => { if (e.key === 'Enter' && shown[0]) { e.preventDefault(); onPick('discuss', shown[0].id); } }}
            className="mx-1 my-1 w-[calc(100%-8px)] rounded-md border border-border-hover bg-bg-input px-2 py-1 text-sm text-text-primary" />
          <div className="max-h-60 overflow-y-auto">
            {people === null && <div className="px-2 py-1.5 text-sm text-text-muted">Loading...</div>}
            {shown.map((p) => (
              <button key={p.id} type="button" role="menuitem" aria-checked={active.persons.has(p.id)} className={item} onClick={() => onPick('discuss', p.id)}>
                <span className="grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold"
                  style={{ background: 'color-mix(in srgb, var(--color-flag-discuss) 22%, transparent)', color: 'var(--color-flag-discuss)' }}>
                  {getInitials(p.name)}
                </span>
                <span className="flex-1 truncate">{p.name}</span>
                {active.persons.has(p.id) && <Check size={14} className="text-success" aria-hidden />}
              </button>
            ))}
            {query.trim() && !exact && people !== null && (
              <button type="button" role="menuitem" className={item} onClick={addPerson}>
                <Plus size={16} className="text-text-secondary" aria-hidden /><span className="truncate">Add "{query.trim()}"</span>
              </button>
            )}
          </div>
        </>
      )}
      {mode === 'new' && renderNewType && (
        <>
          <button type="button" className={item} onClick={() => setMode('kinds')}>
            <ChevronLeft size={16} aria-hidden /><span>New type</span>
          </button>
          {renderNewType(() => setMode('kinds'))}
        </>
      )}
    </div>
    </>,
    document.body,
  );
}
